import { sortBy } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Modules, useLoggedInUserPermissions } from '~/components/Auth';
import { WithPerspectives } from '~/components/MyWorkspace/ManagePerspectives';
import { TableWidgetComponentFactory } from '~/components/MyWorkspace/TableWidget';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { RewardingTabs } from '~/data/tabs';
import { useWorkspaceSettings } from '~/scenes/MyWorkspace/hooks';
import { ICompensationDto } from '~/services/Http/client.people.generated';
import { useGetTeamCompensations } from '~/services/Http/hooks';

import { WidgetProps } from '../../data/widgets';

import { TableHeader } from './components/TableHeader';
import { TeamMemberCompensation } from './components/TeamMemberCompensation';

export interface TeamCompensationWidgetProps
  extends WidgetProps<WithPerspectives> {}

export const TeamCompensationWidgetComponent = TableWidgetComponentFactory({
  TableRowComponent: TeamMemberCompensation,
  getIdentifier: (item: ICompensationDto) => `${item.id}`,
  TableHeaderComponent: TableHeader,
  dataCy: 'team-compensation-widget',
  allTableDialogProps: {
    maxWidth: 'sm',
    fullWidth: true,
  },
});

export const TeamCompensationWidget: FC<TeamCompensationWidgetProps> = ({
  title,
}) => {
  // TODO activeWorkspaceRole should be passed through as part of perspective
  const { activeWorkspaceRole } = useWorkspaceSettings();

  const { t } = useTranslation();
  const getTeamCompensationsReqParams = {
    employeeId: 'me',
    roles: [activeWorkspaceRole],
  };
  const {
    data: compensationsData,
    loading,
    failedToLoad,
  } = useGetTeamCompensations(getTeamCompensationsReqParams);

  const { getEnums } = useAllEnums();
  const salaryFrequencies = useMemo(
    () => getEnums(EnumListKey.EVENT_FREQUENCY),
    [getEnums],
  );
  const getSalaryFrequency = useCallback(
    (code?: string): Enum | undefined => {
      return !!code
        ? salaryFrequencies.find((e) => e.code === code)
        : undefined;
    },
    [salaryFrequencies],
  );
  const compensations: ICompensationDto[] = useMemo(() => {
    const compensations =
      compensationsData?.map(
        ({ salaryFrequency, ...restCompensation }): ICompensationDto => {
          return {
            ...restCompensation,
            salaryFrequency: getSalaryFrequency(salaryFrequency)?.name ?? '',
          };
        },
      ) ?? [];

    return sortBy(
      compensations,
      (compensation) => compensation.employee?.lastName,
    );
  }, [compensationsData, getSalaryFrequency]);

  const permissions = useLoggedInUserPermissions();
  const rewardingModuleEnabledTabs = permissions.enabledTabs[Modules.REWARDING];
  // TODO: Group Salary feature hidden until it has been fixed
  // const {
  //   isVisible: opened,
  //   show: openDialog,
  //   hide: closeDialog,
  // } = useVisible();

  return (
    <>
      <TeamCompensationWidgetComponent
        title={title || t('teamCompensation')}
        data={compensations}
        loading={loading}
        failedToLoad={failedToLoad}
        noDataMessage={t('noCompensation')}
        tableProps={{
          style: {
            minWidth: 310,
          },
        }}
        isTabDisabled={
          !!rewardingModuleEnabledTabs &&
          !rewardingModuleEnabledTabs.includes(RewardingTabs.GENERAL)
        }
        // TODO: Button hidden until Group Salary feature has been fixed
        // customActions={
        //   <Button color="primary" onClick={openDialog}>
        //     {t('groupSalary')}
        //   </Button>
        // }
      />
      {/*<Dialog open={opened} onClose={closeDialog} maxWidth="xl">*/}
      {/*  <DialogContent>*/}
      {/*    <GroupSalary data={compensationsData} />*/}
      {/*  </DialogContent>*/}
      {/*</Dialog>*/}
    </>
  );
};
